import React, { useState, useEffect } from 'react';
// import { useLanguage } from '@/state/language/LanguageProvider';

interface AISettings {
  maxTokensPerConversation: number;
  summarizationStrategy: 'intelligent' | 'simple' | 'none';
  veryOldSummaryType: 'very_brief' | 'brief' | 'detailed';
  oldSummaryType: 'very_brief' | 'brief' | 'detailed';
  recentSummaryType: 'very_brief' | 'brief' | 'detailed';
  modelSelectionStrategy: 'cost_optimized' | 'performance_optimized' | 'balanced';
  defaultModel: string;
  premiumModel: string;
  conversationTimeoutMinutes: number;
  maxConversationLength: number;
}

export const SettingsPage: React.FC = () => {
  // const { t } = useLanguage();
  const [aiSettings, setAiSettings] = useState<AISettings>({
    maxTokensPerConversation: 4000,
    summarizationStrategy: 'intelligent',
    veryOldSummaryType: 'very_brief',
    oldSummaryType: 'brief',
    recentSummaryType: 'detailed',
    modelSelectionStrategy: 'balanced',
    defaultModel: 'gpt-3.5-turbo',
    premiumModel: 'gpt-4',
    conversationTimeoutMinutes: 30,
    maxConversationLength: 50
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAISettings();
  }, []);

  const loadAISettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/ai-settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const settings = await response.json();
        setAiSettings(settings);
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAISettings = async () => {
    try {
      setSaving(true);
      const response = await fetch('/api/v1/ai-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(aiSettings)
      });
      
      if (response.ok) {
        alert('AI settings saved successfully!');
      } else {
        alert('Error saving AI settings');
      }
    } catch (error) {
      console.error('Error saving AI settings:', error);
      alert('Error saving AI settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    try {
      setSaving(true);
      const response = await fetch('/api/v1/ai-settings/reset', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const defaultSettings = await response.json();
        setAiSettings(defaultSettings.settings);
        alert('AI settings reset to defaults!');
      } else {
        alert('Error resetting AI settings');
      }
    } catch (error) {
      console.error('Error resetting AI settings:', error);
      alert('Error resetting AI settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">Settings</h1>
          <p className="mt-1 text-sm text-[var(--color-secondary-text)]">
            Manage your account and system preferences
          </p>
        </div>
        <div className="bg-transparent border border-[var(--color-border)] rounded-lg p-6">
          <p className="text-[var(--color-secondary-text)]">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">Settings</h1>
        <p className="mt-1 text-sm text-[var(--color-secondary-text)]">
          Manage your account and system preferences
        </p>
      </div>
      
      {/* AI Settings Section */}
      <div className="bg-transparent border border-[var(--color-border)] rounded-lg p-6">
        <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">AI Settings</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Max Tokens */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Max Tokens per Conversation
            </label>
            <input
              type="number"
              value={aiSettings.maxTokensPerConversation}
              onChange={(e) => setAiSettings({...aiSettings, maxTokensPerConversation: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-transparent text-[var(--color-secondary-text)]"
              min="1000"
              max="8000"
            />
          </div>

          {/* Summarization Strategy */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Summarization Strategy
            </label>
            <select
              value={aiSettings.summarizationStrategy}
              onChange={(e) => setAiSettings({...aiSettings, summarizationStrategy: e.target.value as any})}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-transparent text-[var(--color-secondary-text)]"
            >
              <option value="intelligent">Intelligent</option>
              <option value="simple">Simple</option>
              <option value="none">None</option>
            </select>
          </div>

          {/* Model Selection Strategy */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Model Selection Strategy
            </label>
            <select
              value={aiSettings.modelSelectionStrategy}
              onChange={(e) => setAiSettings({...aiSettings, modelSelectionStrategy: e.target.value as any})}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-transparent text-[var(--color-secondary-text)]"
            >
              <option value="cost_optimized">Cost Optimized</option>
              <option value="performance_optimized">Performance Optimized</option>
              <option value="balanced">Balanced</option>
            </select>
          </div>

          {/* Conversation Timeout */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Conversation Timeout (minutes)
            </label>
            <input
              type="number"
              value={aiSettings.conversationTimeoutMinutes}
              onChange={(e) => setAiSettings({...aiSettings, conversationTimeoutMinutes: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-transparent text-[var(--color-secondary-text)]"
              min="5"
              max="120"
            />
          </div>

          {/* Max Conversation Length */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Max Conversation Length
            </label>
            <input
              type="number"
              value={aiSettings.maxConversationLength}
              onChange={(e) => setAiSettings({...aiSettings, maxConversationLength: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-transparent text-[var(--color-secondary-text)]"
              min="10"
              max="100"
            />
          </div>

          {/* Default Model */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Default Model
            </label>
            <input
              type="text"
              value={aiSettings.defaultModel}
              onChange={(e) => setAiSettings({...aiSettings, defaultModel: e.target.value})}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-transparent text-[var(--color-secondary-text)]"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mt-6">
          <button
            onClick={saveAISettings}
            disabled={saving}
            className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:bg-[var(--color-primary-bg)]/80 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          
          <button
            onClick={resetToDefaults}
            disabled={saving}
            className="px-4 py-2 bg-[var(--color-secondary-text)] text-[var(--color-accent-text)] rounded-md hover:bg-[var(--color-secondary-text)]/90 disabled:opacity-50"
          >
            {saving ? 'Resetting...' : 'Reset to Defaults'}
          </button>
        </div>
      </div>
    </div>
  );
}; 


